import { CircleCheck, CircleDashed, CircleHelp, History, SearchX, TriangleAlert } from "lucide-react";
import { AppColorVariant } from "../../theme/AppColorVariant";

export enum VersioningState {
  Unknown = "unknown",
  Enabled = "enabled",
  Disabled = "disabled",
  LibraryNotFound = "libraryNotFound",
  CheckFailed = "checkFailed",
}

export enum GraphLibraryType {
  CaseFiles = "caseFiles",
  ProviderFiles = "providerFiles",
  FirmDocuments = "firmDocuments",
  TemplateFiles = "templateFiles",
}

export enum VersioningCheckSource {
  Unknown = "unknown",
  Client = "client",
  Server = "server",
}

export interface GraphLibraryVersioningStatus {
  libraryType: GraphLibraryType;
  configuredDriveName?: string | null;
  versioningState: VersioningState;
  majorVersionLimit?: number | null;
  minorVersionsEnabled?: boolean | null;
  checkedOn?: string | null;
  reportedOn?: string | null;
  failureDetail?: string | null;
  checkSource: VersioningCheckSource;
  stateChangedOn?: string | null;
  /** Name the observer resolved, which may differ from configuredDriveName. */
  libraryName?: string | null;
  /**
   * Whether a client is holding uploads to this library. Null when no client has reported — do not
   * read that as "migrating freely".
   */
  migrationSuspended?: boolean | null;
  overrideApplied?: boolean | null;
  /**
   * Names the site does have registered for this library type, when nothing answers to the configured
   * one. Lets a repointed or mistyped root drive name explain itself instead of dead-ending.
   */
  registeredNamesOnSite?: string[] | null;
  /**
   * Sites this library type is registered on, when the configuration's own site matched none of them —
   * typically because the site was provisioned under a name SharePoint suffixed.
   */
  registeredSiteNames?: string[] | null;
}

export interface GraphLibraryVersioningCheck {
  status: GraphLibraryVersioningStatus;
  /**
   * True when the server read SharePoint itself. False when no direct read was possible, leaving
   * the carried state as whatever was already on record.
   */
  wasReadDirectly: boolean;
  /** Why no direct read was possible, naming the setting to correct. Absent when one was taken. */
  unavailableReason?: string | null;
}

/**
 * Version cap SharePoint is put on when versioning is switched on from the portal.
 *
 * Shown to an operator before they confirm, so it has to say what the server will actually send —
 * `SharepointVersioningWriter.MajorVersionLimitOnEnable`. The cap is fixed rather than restored because
 * a library's previous cap is not recorded once versioning has been off.
 */
export const MAJOR_VERSION_LIMIT_ON_ENABLE = 500;

export const VersioningCheckSourceLabels: Record<VersioningCheckSource, string> = {
  [VersioningCheckSource.Unknown]: "unestablished",
  [VersioningCheckSource.Client]: "reported by client",
  [VersioningCheckSource.Server]: "read by server",
};

export interface ClientConfigurationVersioningStatus {
  clientConfigurationId: string;
  clientConfigurationName?: string | null;
  /** Clients with no configuration assigned fall back to this one, so problems here reach further. */
  isDefault?: boolean;
  /** Site the configured drive names were resolved against. */
  sharepointRootSiteName?: string | null;
  /**
   * Whether the server could read versioning for this configuration if asked. False means a check
   * reads nothing and queues nothing, so a refresh control must not be offered as though it would.
   */
  serverCanRead?: boolean;
  /** What prevents the server reading, naming the setting to correct. Absent when it can. */
  serverUnavailableReason?: string | null;
  libraries: GraphLibraryVersioningStatus[];
}

export const GraphLibraryTypeLabels: Record<GraphLibraryType, string> = {
  [GraphLibraryType.CaseFiles]: "Cases",
  [GraphLibraryType.ProviderFiles]: "Providers",
  [GraphLibraryType.FirmDocuments]: "Firm Documents",
  [GraphLibraryType.TemplateFiles]: "Template Files",
};

export const VersioningStateLabels: Record<VersioningState, string> = {
  [VersioningState.Unknown]: "Not reported",
  [VersioningState.Enabled]: "Versioning on",
  [VersioningState.Disabled]: "Versioning off",
  [VersioningState.LibraryNotFound]: "Library not found",
  [VersioningState.CheckFailed]: "Check failed",
};

/**
 * Versioning left on is the state that needs attention — the client suspends its own work on such a
 * library — so it reads as a warning while an off library is the settled, healthy case.
 */
export const VersioningStateColors: Record<VersioningState, AppColorVariant> = {
  [VersioningState.Unknown]: "neutral",
  [VersioningState.Enabled]: "warning",
  [VersioningState.Disabled]: "success",
  [VersioningState.LibraryNotFound]: "danger",
  [VersioningState.CheckFailed]: "danger",
};

/** Paired with the colour so the state is legible without relying on colour perception. */
export const VersioningStateIcons: Record<VersioningState, React.ElementType> = {
  [VersioningState.Unknown]: CircleHelp,
  [VersioningState.Enabled]: History,
  [VersioningState.Disabled]: CircleCheck,
  [VersioningState.LibraryNotFound]: SearchX,
  [VersioningState.CheckFailed]: TriangleAlert,
};

export const VersioningStateDescriptions: Record<VersioningState, string> = {
  [VersioningState.Unknown]:
    "No client has reported on this library yet. The setting is undetermined — it is not assumed to be off.",
  [VersioningState.Enabled]:
    "SharePoint keeps version history for this library. The client suspends migration into it until versioning is turned off.",
  [VersioningState.Disabled]: "SharePoint keeps no version history for this library.",
  [VersioningState.LibraryNotFound]:
    "This name was checked against the SharePoint site and no matching library was found. It may have been renamed or deleted.",
  [VersioningState.CheckFailed]:
    "The client reached SharePoint but could not determine the setting.",
};

/** States that leave the setting undetermined, rather than establishing it either way. */
export const isVersioningUndetermined = (state: VersioningState): boolean =>
  state === VersioningState.Unknown ||
  state === VersioningState.LibraryNotFound ||
  state === VersioningState.CheckFailed;

/**
 * Whether nothing has ever looked at this library — no client report, no server read.
 *
 * A status row materialized by the projection carries no source and no timestamps, because it
 * describes a configured drive name that resolved to nothing rather than an observation of SharePoint.
 */
export const isVersioningNeverObserved = (status: GraphLibraryVersioningStatus): boolean =>
  status.checkSource === VersioningCheckSource.Unknown &&
  !status.reportedOn &&
  !status.checkedOn;

export interface VersioningPresentation {
  label: string;
  color: AppColorVariant;
  icon: React.ElementType;
  description: string;
}

/**
 * How one status should read on screen.
 *
 * `libraryNotFound` covers two situations the server cannot distinguish in the enum but that need
 * opposite advice. The projection emits it for a configured drive name that matches no registration —
 * which is the ordinary state before a client has ever run, since clients register their libraries as
 * they discover them. An observer that actually reached SharePoint and found nothing emits the same
 * value, and that one really does mean the library is missing or renamed. Telling an operator to go
 * check the name in SharePoint when no client has run yet sends them hunting for a mistake nobody
 * made, so the two are separated here by whether anything ever looked.
 */
export const versioningPresentation = (
  status: GraphLibraryVersioningStatus,
): VersioningPresentation => {
  if (
    status.versioningState === VersioningState.LibraryNotFound &&
    isVersioningNeverObserved(status)
  ) {
    const registered = (status.registeredNamesOnSite ?? []).filter(Boolean);
    const sites = (status.registeredSiteNames ?? []).filter(Boolean);

    // Naming what does exist turns a dead end into a diagnosis. The two failures need different
    // actions: a name that matches nothing on the right site is usually a repointed or mistyped drive
    // name, whereas libraries sitting on a site this configuration does not recognise means the site
    // itself is the mismatch — which happens when SharePoint provisioned it under a suffixed name.
    let description: string;
    if (registered.length > 0) {
      description =
        `No library is registered under this name. This site has ${registered.join(", ")} ` +
        "registered for this target, so either the configured name needs to match one of those, or a " +
        "client has yet to register the library this name refers to.";
    } else if (sites.length > 0) {
      description =
        `No library is registered on this configuration's site. This target is registered on ` +
        `${sites.join(", ")}, so the configured SharePoint site name does not match the site the ` +
        "libraries were actually provisioned into.";
    } else {
      description =
        "No client has registered a library under this name yet. Clients register their libraries " +
        "when they first discover them, so this is expected before a client has run. If it persists " +
        "once a client is active, check the name matches the library in SharePoint.";
    }

    return {
      label: "Not registered",
      color: "neutral",
      icon: CircleDashed,
      description,
    };
  }

  return {
    label: VersioningStateLabels[status.versioningState],
    color: VersioningStateColors[status.versioningState] ?? "neutral",
    icon: VersioningStateIcons[status.versioningState],
    description: VersioningStateDescriptions[status.versioningState],
  };
};

/**
 * A drive nobody configured, as opposed to a configured name that resolved to nothing.
 *
 * The server reports both as libraryNotFound because both fail to resolve, but they are different
 * problems: one is an empty field, the other a name that does not match the SharePoint site. Telling
 * an operator a library is missing when they simply never named one sends them looking in SharePoint.
 */
export const isVersioningTargetUnconfigured = (status: GraphLibraryVersioningStatus): boolean =>
  status.versioningState === VersioningState.LibraryNotFound &&
  !status.configuredDriveName?.trim();

/**
 * When the displayed state was last established.
 *
 * `reportedOn` is stamped by the server every time an observation lands and only ever moves forward;
 * `checkedOn` carries the observer's own clock and can be absent even on a fresh report, so reading it
 * alone shows "never checked" for libraries a client has reported on perfectly well.
 */
export const versioningObservedOn = (
  status: GraphLibraryVersioningStatus,
): string | null => status.reportedOn ?? status.checkedOn ?? null;

/** Parsed override set. Case-insensitive: the column's casing is whatever a caller last wrote. */
export const parseVersioningOverrides = (value?: string | null): GraphLibraryType[] => {
  if (!value) return [];

  const byLowerCase = new Map(
    Object.values(GraphLibraryType).map((type) => [type.toLowerCase(), type]),
  );

  return Array.from(
    new Set(
      value
        .split(",")
        .map((entry) => byLowerCase.get(entry.trim().toLowerCase()))
        .filter((type): type is GraphLibraryType => !!type),
    ),
  );
};

/** Null rather than an empty string, so clearing every override clears the column. */
export const serializeVersioningOverrides = (types: GraphLibraryType[]): string | null =>
  types.length === 0 ? null : Array.from(new Set(types)).join(",");

export interface VersioningRollup {
  /** Distinct libraries with versioning on. */
  versionedCount: number;
  suspendedCount: number;
  /** Configured drives whose state is not established either way. */
  undeterminedCount: number;
  compliantCount: number;
  /** Drives with no name configured — excluded from every other count. */
  unconfiguredCount: number;
  /** Configurations with at least one versioned library. */
  affectedConfigurationCount: number;
  configurationCount: number;
  /** Distinct libraries the counts above are drawn from. */
  consideredCount: number;
  /** Most recent observation across the organization, or null when nothing was ever established. */
  newestObservedOn: string | null;
}

/**
 * Organization-wide versioning counts, taken across every client configuration.
 *
 * Counts distinct libraries, not rows. Registrations are organization-wide, so two configurations
 * naming the same drive on the same site resolve to one status row and would otherwise be counted
 * twice — reporting two versioned libraries where SharePoint holds one.
 */
export const rollUpVersioning = (
  configurations: ClientConfigurationVersioningStatus[],
): VersioningRollup => {
  const seen = new Set<string>();
  const rollup: VersioningRollup = {
    versionedCount: 0,
    suspendedCount: 0,
    undeterminedCount: 0,
    compliantCount: 0,
    unconfiguredCount: 0,
    affectedConfigurationCount: 0,
    configurationCount: configurations.length,
    consideredCount: 0,
    newestObservedOn: null,
  };

  for (const configuration of configurations) {
    let configurationIsAffected = false;

    for (const library of configuration.libraries ?? []) {
      if (isVersioningTargetUnconfigured(library)) {
        rollup.unconfiguredCount += 1;
        continue;
      }

      const identity = [
        configuration.sharepointRootSiteName?.trim().toLowerCase() ?? "",
        (library.libraryName ?? library.configuredDriveName ?? library.libraryType)
          .trim()
          .toLowerCase(),
      ].join("/");

      if (library.versioningState === VersioningState.Enabled) configurationIsAffected = true;

      if (seen.has(identity)) continue;
      seen.add(identity);

      rollup.consideredCount += 1;

      if (library.versioningState === VersioningState.Enabled) rollup.versionedCount += 1;
      else if (library.versioningState === VersioningState.Disabled) rollup.compliantCount += 1;
      else rollup.undeterminedCount += 1;

      if (library.migrationSuspended === true) rollup.suspendedCount += 1;

      const observedOn = versioningObservedOn(library);
      if (observedOn && (!rollup.newestObservedOn || observedOn > rollup.newestObservedOn)) {
        rollup.newestObservedOn = observedOn;
      }
    }

    if (configurationIsAffected) rollup.affectedConfigurationCount += 1;
  }

  return rollup;
};
